// screens/CockpitHome.tsx — H1a (COCKPIT-HOME-COLUMNS-SPEC.md §H1): the
// cockpit's front page at every width, decision `c0d0480a` amended by Huan
// 2026-10-15 (the Atlas pane is on the RIGHT here too — CockpitFrame
// already renders it; this screen is only ever the LEFT content).
//
// Gauges row (one thin ring per `meters.facts` entry with a cap, the SAME
// sentence CockpitToday.tsx states on tap — lib/meterSentence.ts, extracted
// so the two screens cannot drift) above a horizontal, scroll-snapping row
// of columns, one per the registry `GET /api/cockpit/columns` answers
// (no rows yet for any tenant in this chunk — the default personal set,
// core's own `DEFAULT_PERSONAL_COLUMNS`, is what every tenant actually
// reads). Cards ride EXISTING data only (lib/cockpitBoard.ts's own header):
// `/api/today`'s groups for six of the eight default columns, plus the
// SAME `listAgenda`/`getAudienceSummary` calls CockpitCalendar/Audience
// already make for Calendar/Audience — never a new card server. Radar and
// Record carry no such existing summary read cheap enough to borrow, so
// their column reads its honest empty state until a later chunk feeds it
// (reported as a judgement call, not silently guessed at).
//
// H1b (H1B-BOARD-ACTS-BRIEF.md, COCKPIT-HOME-COLUMNS-SPEC.md §H1 "Acts" +
// "Receipts and Undo") adds this chunk: the focus card's acts (Approve/Not
// now, Reconnect, Open/Done for now, Yes/Open — lib/cockpitBoard.ts's
// `actsForKind`), each calling the SAME server function the row's own
// screen already calls (approveProposal/approveProgramme/
// reconnectGoogleConnection — never a parallel implementation); press-
// and-hold for the two consequential acts; a done card that STAYS, styled
// with the word, Undo where a reverse exists (fixed 10s), and expires
// after lever `cockpit.board.done_ttl_minutes` (default 5, read from
// GET /api/levers).
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  ApiError,
  approveProgramme,
  approveProposal,
  getAudienceSummary,
  getCockpitColumns,
  getLevers,
  getSystemsSummary,
  getToday,
  listAgenda,
  reconnectGoogleConnection,
  type CockpitColumn,
  type SystemsSummaryApi,
  type TodayBriefing,
  type TodaySystemFact,
} from '../lib/api';
import { comingUpDayLabel, dayOf, timeOf } from '../lib/calendarItems';
import { actsForKind, cardsForColumn, emptyStatementFor, sortColumns, sortReason, type BoardCard } from '../lib/cockpitBoard';
import { meterSentence } from '../lib/meterSentence';
import { readCockpitPlace, writeCockpitPlace } from '../lib/cockpitPlace';
import './CockpitHome.css';

export interface CockpitHomeProps {
  tenantId: string;
  onNavigate: (path: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; briefing: TodayBriefing; columns: CockpitColumn[]; systemsSummary: SystemsSummaryApi };

interface NextEvent {
  title: string;
  whenLabel: string;
  startsAt: string;
}

/** H1b — the press-and-hold floor ("the floor for consequential items",
 * the brief's own number) and the Undo window on a reversible act (the
 * Acts table's own "Undo · 10 s" — fixed, never the TTL lever). */
const HOLD_MS = 600;
const UNDO_MS = 10_000;
const DEFAULT_DONE_TTL_MINUTES = 5;

/** A card once an act resolved — it STAYS, styled done with the word
 * (COCKPIT-HOME-COLUMNS-SPEC.md §H1 "Receipts and Undo"). */
interface DoneEntry {
  text: string;
  canUndo: boolean;
  undoVisible: boolean;
}

function nextEventFrom(events: Array<{ title: string | null; startsAt: string; startLocal: string | null; status: string }>): NextEvent | null {
  const now = Date.now();
  const upcoming = events
    .filter((e) => e.status !== 'cancelled' && new Date(e.startsAt).getTime() >= now)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
  const e = upcoming[0];
  if (!e) return null;
  const day = dayOf(e.startLocal ?? e.startsAt);
  const time = timeOf(e.startLocal ?? e.startsAt);
  const whenLabel = [day ? comingUpDayLabel(day) : null, time].filter(Boolean).join(' · ') || 'Soon';
  return { title: e.title ?? '(untitled)', whenLabel, startsAt: e.startsAt };
}

/** The ring's own short inner reading ("48 h", "62%") — round numbers only,
 * the exact figure is the tap sentence's job (meterSentence). A percent
 * unit reads as a percent; anything else reads as "<n> <unit>". */
function gaugeValueText(fact: TodaySystemFact): string {
  if (fact.valueNum === null) return '—';
  const unit = (fact.unit ?? '').trim();
  if (unit === '%') return `${Math.round(fact.valueNum)}%`;
  return unit ? `${Math.round(fact.valueNum)} ${unit}` : `${Math.round(fact.valueNum)}`;
}

/** Design review round 2 (r2-f): a ring like the concept's — the short
 * value INSIDE the ring, the label below it, the arc showing fill against
 * the cap (full ring, in the warning token, at or over cap). Never colour
 * alone (voice canon §2.7): the word (on tap, meterSentence) still carries
 * the cap/stale state — the ring's fill and the word say the same thing in
 * two registers, neither is the only signal. */
function Gauge({ fact, open, onToggle }: { fact: TodaySystemFact; open: boolean; onToggle: () => void }) {
  const ratio =
    fact.valueNum !== null && fact.capNum !== null && fact.capNum > 0
      ? Math.max(0, Math.min(1, fact.valueNum / fact.capNum))
      : 0;
  // r2-f round 3: the ring is sized by CSS (.chome__gauge-ring, 48px /
  // 40px narrow) — the SVG fills it via width/height 100% rather than a
  // fixed pixel attribute, so the one viewBox serves both sizes.
  const r = 20;
  const c = 2 * Math.PI * r;
  const tone = fact.stale ? 'stale' : fact.meter === 'at_cap' ? 'cap' : fact.meter === 'near' ? 'near' : 'ok';
  return (
    <div class={`chome__gauge-wrap chome__gauge-wrap--${tone}`}>
      <button
        type="button"
        class="chome__gauge"
        aria-expanded={open}
        aria-label={meterSentence(fact)}
        onClick={onToggle}
      >
        <span class="chome__gauge-ring">
          <svg width="100%" height="100%" viewBox="0 0 48 48" aria-hidden="true">
            <circle cx="24" cy="24" r={r} class="chome__gauge-track" />
            <circle
              cx="24"
              cy="24"
              r={r}
              class="chome__gauge-fill"
              stroke-dasharray={`${c}`}
              stroke-dashoffset={`${c * (1 - ratio)}`}
              transform="rotate(-90 24 24)"
            />
          </svg>
          <span class="chome__gauge-value" aria-hidden="true">{gaugeValueText(fact)}</span>
        </span>
        <span class="chome__gauge-label">{fact.label}</span>
      </button>
      {/* In normal flow, not absolutely positioned: a gauges row that also
       * scrolls horizontally (overflow-x: auto) forces overflow-y to auto
       * too (the CSS spec's own overflow-shorthand rule), which CLIPPED an
       * absolutely-positioned sentence the first time this was walked —
       * found in the walk's own screenshot, fixed by keeping it in flow. */}
      {open ? <div class="chome__gauge-sentence" role="status">{meterSentence(fact)}</div> : null}
    </div>
  );
}

/** H1b — the consequential-act button: ~600ms of holding before the one
 * call fires, a visible fill on the button itself (CSS, keyed off the
 * `--pressed` class), releasing early does nothing. Keyboard: Space/Enter
 * held the same duration — both keys' own native activation (click on
 * keyup/keydown) is suppressed so holding is the ONLY way to fire it. The
 * label states what you get ("Hold to approve") — never a caption beside
 * a plain "Approve" button (voice canon §2.5, no instructional captions). */
function HoldButton({ label, disabled, onHold }: { label: string; disabled?: boolean; onHold: () => void }) {
  const [pressed, setPressed] = useState(false);
  const timerRef = useRef<number | null>(null);

  function clear() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }
  function start() {
    if (disabled || timerRef.current !== null) return;
    setPressed(true);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setPressed(false);
      onHold();
    }, HOLD_MS);
  }
  function cancel() {
    clear();
    setPressed(false);
  }

  return (
    <button
      type="button"
      class={`chome__holdbtn${pressed ? ' chome__holdbtn--pressed' : ''}`}
      style={pressed ? ({ '--hold-ms': `${HOLD_MS}ms` } as Record<string, string>) : undefined}
      disabled={disabled}
      onPointerDown={(e: Event) => {
        e.preventDefault();
        start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.repeat) return;
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        start();
      }}
      onKeyUp={(e: KeyboardEvent) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        e.preventDefault();
        cancel();
      }}
    >
      <span class="chome__holdbtn-label">{label}</span>
    </button>
  );
}

/** r2-f's own two card registers: the FOCUS card (top of a column) is
 * stretched, its acts stacked full-width pills below the text; every other
 * card is compact — title, line, and a small round "›" Open in the
 * bottom-right corner, the concept's own shape. `needsYou` carries BOTH a
 * glow (soft outer shadow + 1.5px accent border, `--board-glow-*`) and the
 * small pill label — never colour alone.
 *
 * H1b: an acted-on card renders its DONE register instead — the receipt
 * replaces title/line entirely (f3-3's own shape: the sentence, Undo
 * beneath it where a reverse exists), never the "Needs you" pill (it no
 * longer does). Every card, done or not, keeps its small round Open — H1's
 * own floor ("every card can Open"). */
function BoardCardView({
  card,
  stretched,
  onOpen,
  done,
  error,
  busy,
  onApproveProposal,
  onApproveProgramme,
  onDoneForNow,
  onUndo,
  onReconnect,
}: {
  card: BoardCard;
  stretched: boolean;
  onOpen: () => void;
  done: DoneEntry | undefined;
  error: string | undefined;
  busy: boolean;
  onApproveProposal: () => void;
  onApproveProgramme: () => void;
  onDoneForNow: () => void;
  onUndo: () => void;
  onReconnect: () => void;
}) {
  if (done) {
    return (
      <div
        class={`chome__card${stretched ? ' chome__card--focus' : ' chome__card--compact'} chome__card--done`}
        data-testid={`card-${card.id}`}
      >
        <div class="chome__card-receipt">{done.text}</div>
        {done.canUndo && done.undoVisible ? (
          <button type="button" class="chome__card-undo" onClick={onUndo}>
            Undo
          </button>
        ) : null}
        <button type="button" class="chome__card-round" aria-label={`Open ${card.title}`} onClick={onOpen}>
          ›
        </button>
      </div>
    );
  }

  const acts = actsForKind(card.kind);

  return (
    <div
      class={`chome__card${stretched ? ' chome__card--focus' : ' chome__card--compact'}${
        card.needsYou ? ' chome__card--needs-you' : ''
      }`}
      data-testid={`card-${card.id}`}
    >
      {card.needsYou ? <span class="chome__pill">Needs you</span> : null}
      <div class="chome__card-title">{card.title}</div>
      {card.line ? <div class="chome__card-line">{card.line}</div> : null}
      {error ? (
        <div class="chome__card-error" role="status">
          {error}
        </div>
      ) : null}
      {stretched ? (
        <div class="chome__card-acts">
          {acts.map((act) => {
            switch (act) {
              case 'approve_proposal':
                return <HoldButton key={act} label="Hold to approve" disabled={busy} onHold={onApproveProposal} />;
              case 'approve_programme':
                return <HoldButton key={act} label="Hold to approve" disabled={busy} onHold={onApproveProgramme} />;
              case 'not_now':
                return (
                  <button key={act} type="button" class="chome__pillbtn" onClick={onOpen}>
                    Not now
                  </button>
                );
              case 'reconnect':
                return (
                  <button
                    key={act}
                    type="button"
                    class="chome__pillbtn chome__pillbtn--primary"
                    disabled={busy}
                    onClick={onReconnect}
                  >
                    {card.title}
                  </button>
                );
              case 'done_for_now':
                return (
                  <button key={act} type="button" class="chome__pillbtn" onClick={onDoneForNow}>
                    Done for now
                  </button>
                );
              case 'open':
                return (
                  <button key={act} type="button" class="chome__pillbtn chome__pillbtn--primary" onClick={onOpen}>
                    Open
                  </button>
                );
              default:
                return null;
            }
          })}
        </div>
      ) : (
        <button type="button" class="chome__card-round" aria-label={`Open ${card.title}`} onClick={onOpen}>
          ›
        </button>
      )}
    </div>
  );
}

interface ColumnActs {
  doneById: Record<string, DoneEntry>;
  errorById: Record<string, string>;
  busyId: string | null;
  onApproveProposal: (card: BoardCard) => void;
  onApproveProgramme: (card: BoardCard) => void;
  onDoneForNow: (card: BoardCard) => void;
  onUndo: (card: BoardCard) => void;
  onReconnect: (card: BoardCard) => void;
}

function ColumnView({
  column,
  cards,
  reason,
  reasonOpen,
  focusIndex,
  acts,
  onOpenScreen,
  onOpenCard,
  onNextCard,
  onShowReason,
  onHideReason,
}: {
  column: CockpitColumn;
  cards: BoardCard[];
  reason: string | null;
  reasonOpen: boolean;
  focusIndex: number;
  acts: ColumnActs;
  onOpenScreen: () => void;
  onOpenCard: (card: BoardCard) => void;
  onNextCard: () => void;
  onShowReason: () => void;
  onHideReason: () => void;
}) {
  const pressTimer = useRef<number | null>(null);

  function startPress() {
    if (!reason) return;
    pressTimer.current = window.setTimeout(onShowReason, 500);
  }
  function endPress() {
    if (pressTimer.current !== null) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  // H1b — a done card never takes the focus slot while undone cards
  // remain (the brief's own "Cycle" rule): "Next card" cycles the FOCUS
  // only among the undone pool; once every card in the column is done,
  // the pool falls back to all of them rather than showing nothing.
  const notDone = cards.filter((c) => !acts.doneById[c.id]);
  const pool = notDone.length > 0 ? notDone : cards;
  const effectiveFocus = pool.length > 0 ? focusIndex % pool.length : 0;
  const focusCard = pool[effectiveFocus] ?? null;
  // The done card "cycles to the back" — rendered after every undone card
  // still in the rest list, never ahead of one.
  const restNotDone = cards.filter((c) => c.id !== focusCard?.id && !acts.doneById[c.id]);
  const restDone = cards.filter((c) => c.id !== focusCard?.id && acts.doneById[c.id]);
  const rest = [...restNotDone, ...restDone];

  function viewFor(card: BoardCard, stretched: boolean) {
    return (
      <BoardCardView
        card={card}
        stretched={stretched}
        onOpen={() => onOpenCard(card)}
        done={acts.doneById[card.id]}
        error={acts.errorById[card.id]}
        busy={acts.busyId === card.id}
        onApproveProposal={() => acts.onApproveProposal(card)}
        onApproveProgramme={() => acts.onApproveProgramme(card)}
        onDoneForNow={() => acts.onDoneForNow(card)}
        onUndo={() => acts.onUndo(card)}
        onReconnect={() => acts.onReconnect(card)}
        key={card.id}
      />
    );
  }

  return (
    <div class="chome__col" data-testid={`column-${column.id}`}>
      <div class="chome__col-head">
        <button
          type="button"
          class="chome__col-title"
          onClick={onOpenScreen}
          onContextMenu={(e) => {
            if (!reason) return;
            e.preventDefault();
            onShowReason();
          }}
          onPointerDown={startPress}
          onPointerUp={endPress}
          onPointerLeave={endPress}
        >
          {column.title}
        </button>
        {/* ⚑ atlas-core review round 3: a quiet round control beside the
         * title, never a loose word between cards (the concept has no
         * stray links) — cycles which card the focus slot shows. */}
        {cards.length > 1 ? (
          <button type="button" class="chome__col-next" aria-label="Next card" onClick={onNextCard}>
            ›
          </button>
        ) : null}
      </div>
      {reasonOpen && reason ? (
        <div class="chome__col-reason" role="status" onClick={onHideReason}>
          {reason}
        </div>
      ) : null}
      <div class="chome__col-body">
        {cards.length === 0 ? (
          <div class="chome__empty">
            <div class="chome__empty-word">Nothing needs you.</div>
            <div class="chome__empty-sub">{emptyStatementFor(column)}</div>
          </div>
        ) : (
          <>
            {focusCard ? viewFor(focusCard, true) : null}
            {rest.map((card) => viewFor(card, false))}
          </>
        )}
      </div>
    </div>
  );
}

export function CockpitHome({ tenantId, onNavigate }: CockpitHomeProps) {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [nextEvent, setNextEvent] = useState<NextEvent | null>(null);
  const [audienceUnanswered, setAudienceUnanswered] = useState<number | undefined>(undefined);
  const [focusByColumn, setFocusByColumn] = useState<Record<string, number>>({});
  const [reasonOpenFor, setReasonOpenFor] = useState<string | null>(null);
  const [openGauge, setOpenGauge] = useState<string | null>(null);
  const [focusColumnId, setFocusColumnId] = useState<string | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  // H2 (COCKPIT-HOME-COLUMNS-SPEC.md §H2) — "the board restores its own
  // row scroll and focus column on return". Guards WHEN the restore/persist
  // below are allowed to run: false until the columns have actually
  // loaded, so the first mount's own `focusColumnId === null` can never be
  // written out and clobber a place kept from an earlier visit before the
  // read below gets its one chance at it.
  const boardPlaceRestored = useRef(false);

  // H1b — Acts state. Kept at this level (not in lib/cockpitBoard.ts,
  // which stays pure data — its own header) because it is UI lifecycle:
  // in-flight/error/done per card, and the timers that carry a done card
  // from its receipt to Undo's close to its own expiry.
  const [doneById, setDoneById] = useState<Record<string, DoneEntry>>({});
  const [errorById, setErrorById] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  // A card id whose done receipt has EXPIRED (the TTL lever) — filtered
  // out of its column entirely from then on ("it expires", §H1 "Receipts
  // and Undo"). A thread card's own "Done for now" carries the SAME
  // meaning the Acts table gives it ("hides the card until the next
  // arrival on that thread"): nothing in this chunk re-fetches `/api/today`
  // after mount, so there is no later read whose `at` could move past the
  // snapshot and prove a genuine new arrival — the day a later chunk adds
  // that refetch, a fresh (different `at`) thread card naturally gets a
  // NEW id's worth of nothing hidden, since this set is keyed by card id,
  // not thread id (a judgement call, reported here rather than built
  // speculatively against a mechanism that doesn't exist yet).
  const [hiddenCardIds, setHiddenCardIds] = useState<Record<string, true>>({});
  const [doneTtlMinutes, setDoneTtlMinutes] = useState(DEFAULT_DONE_TTL_MINUTES);
  const timersRef = useRef<Record<string, { ttl?: number; undo?: number }>>({});

  useEffect(() => {
    let cancelled = false;
    // H3b — the systems summary, fetched once alongside Today and cached
    // for the board's lifetime (same posture as `columns`): the project
    // columns' own top card reads its `projects` section
    // (lib/cockpitBoard.ts's `findProjectFact`).
    Promise.all([getToday(tenantId), getCockpitColumns(tenantId), getSystemsSummary(tenantId)])
      .then(([briefing, { columns }, systemsSummary]) => {
        if (!cancelled) setState({ phase: 'ready', briefing, columns, systemsSummary });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ phase: 'error', message: err instanceof ApiError ? err.message : 'the board could not be read' });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // H2 — "the board restores its own row scroll and focus column on
  // return": the ONE attempt, the first time the columns are actually
  // known (so a restored focusColumnId can be checked against a REAL
  // column id, never applied blind). `boardPlaceRestored` both marks that
  // attempt done AND gates the persist effect below, so that effect's own
  // first (mount-time, `focusColumnId === null`) run can never fire before
  // this one has had its turn — see the ref's own comment above.
  useEffect(() => {
    if (boardPlaceRestored.current || state.phase !== 'ready') return;
    boardPlaceRestored.current = true;
    const place = readCockpitPlace<{ focusColumnId?: string | null; rowScrollLeft?: number }>('home');
    if (!place) return;
    if (place.focusColumnId && state.columns.some((c) => c.id === place.focusColumnId)) {
      setFocusColumnId(place.focusColumnId);
    }
    if (typeof place.rowScrollLeft === 'number' && rowRef.current) {
      rowRef.current.scrollLeft = place.rowScrollLeft;
    }
  }, [state]);

  // The other half: persist whenever the focus column actually changes
  // (⌘←/⌘→, the ‹/› edge controls, a column title's own "Next card" never
  // touches this — that's a card within a column, not which column). A
  // pure row-drag scroll with no focus change is caught by the row's own
  // `onScroll` below instead, since this effect's dependency wouldn't fire
  // for that.
  useEffect(() => {
    if (!boardPlaceRestored.current) return;
    writeCockpitPlace('home', { focusColumnId, rowScrollLeft: rowRef.current?.scrollLeft ?? 0 });
  }, [focusColumnId]);

  // H1b — the TTL lever, read once (same precedent as cockpit.week_hours:
  // an owner-tunable number, never a constant). A read that fails, or
  // carries no number, keeps the catalogue's own default (5) rather than
  // guessing a different one here.
  useEffect(() => {
    let cancelled = false;
    getLevers(tenantId)
      .then(({ levers }) => {
        if (cancelled) return;
        const row = levers.find((l) => l.key === 'cockpit.board.done_ttl_minutes');
        const v = row ? (row.currentValue ?? row.defaultValue) : null;
        if (typeof v === 'number' && Number.isFinite(v) && v > 0) setDoneTtlMinutes(v);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Calendar's own top card — the same `listAgenda` call CockpitCalendar
  // already makes, narrowed to the next 24h. A failed read costs the
  // Calendar column its card, never the board.
  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const to = new Date(now.getTime() + 24 * 3_600_000);
    void listAgenda(tenantId, { from: now.toISOString(), to: to.toISOString(), includeCancelled: false })
      .then((r) => {
        if (!cancelled) setNextEvent(nextEventFrom(r.events));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Audience's own top card — the same `getAudienceSummary` call the
  // Audience screen already makes.
  useEffect(() => {
    let cancelled = false;
    void getAudienceSummary(tenantId)
      .then((r) => {
        if (!cancelled) setAudienceUnanswered(r.tiles.unanswered);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Every timer this screen owns is cleared on unmount — a background
  // `setTimeout` reaching a stale setState is exactly the kind of leak a
  // walk's own browser console would catch silently.
  useEffect(() => {
    return () => {
      for (const t of Object.values(timersRef.current)) {
        if (t.ttl !== undefined) window.clearTimeout(t.ttl);
        if (t.undo !== undefined) window.clearTimeout(t.undo);
      }
      timersRef.current = {};
    };
  }, []);

  function clearCardTimers(cardId: string) {
    const t = timersRef.current[cardId];
    if (!t) return;
    if (t.ttl !== undefined) window.clearTimeout(t.ttl);
    if (t.undo !== undefined) window.clearTimeout(t.undo);
    delete timersRef.current[cardId];
  }

  /** Marks a card done — the WORD stays on the card (`text`), Undo shows
   * for 10s when a reverse exists, and the whole thing cycles out of the
   * column after `doneTtlMinutes` (the brief's "Receipts and Undo"). */
  function markDone(card: BoardCard, text: string, canUndo: boolean) {
    clearCardTimers(card.id);
    setErrorById((cur) => (cur[card.id] ? { ...cur, [card.id]: '' } : cur));
    setDoneById((cur) => ({ ...cur, [card.id]: { text, canUndo, undoVisible: canUndo } }));
    const ttlTimer = window.setTimeout(
      () => {
        setDoneById((cur) => {
          if (!(card.id in cur)) return cur;
          const next = { ...cur };
          delete next[card.id];
          return next;
        });
        setHiddenCardIds((cur) => ({ ...cur, [card.id]: true }));
        delete timersRef.current[card.id];
      },
      Math.max(1, doneTtlMinutes) * 60_000
    );
    let undoTimer: number | undefined;
    if (canUndo) {
      undoTimer = window.setTimeout(() => {
        setDoneById((cur) => {
          const entry = cur[card.id];
          if (!entry) return cur;
          return { ...cur, [card.id]: { ...entry, undoVisible: false } };
        });
      }, UNDO_MS);
    }
    timersRef.current[card.id] = { ttl: ttlTimer, undo: undoTimer };
  }

  function clearDone(card: BoardCard) {
    clearCardTimers(card.id);
    setDoneById((cur) => {
      if (!(card.id in cur)) return cur;
      const next = { ...cur };
      delete next[card.id];
      return next;
    });
  }

  function sentenceFor(err: unknown, fallback: string): string {
    return err instanceof ApiError ? (err.serverMessage ?? err.message) : fallback;
  }

  async function handleApproveProposal(card: BoardCard) {
    if (!card.proposalId) return;
    setBusyId(card.id);
    try {
      await approveProposal(tenantId, card.proposalId);
      // The brief's own words, verbatim — no reverse (approval runs
      // programmes): "Approved · <title> · open Review".
      markDone(card, `Approved · ${card.title} · open Review`, false);
    } catch (err) {
      setErrorById((cur) => ({ ...cur, [card.id]: sentenceFor(err, 'the approval was not recorded') }));
    } finally {
      setBusyId(null);
    }
  }

  async function handleApproveProgramme(card: BoardCard) {
    if (!card.programmeId) return;
    setBusyId(card.id);
    try {
      const { receipt } = await approveProgramme(tenantId, card.programmeId);
      markDone(card, receipt, false);
    } catch (err) {
      setErrorById((cur) => ({ ...cur, [card.id]: sentenceFor(err, 'the approval was not recorded') }));
    } finally {
      setBusyId(null);
    }
  }

  function handleDoneForNow(card: BoardCard) {
    // LOCAL — no server call (the brief's own word): the thread stays
    // "waiting on me" server-side; this is the board remembering it was
    // set aside until the thread itself moves.
    markDone(card, `Done — ${card.title}.`, true);
  }

  function handleUndo(card: BoardCard) {
    clearDone(card);
  }

  function handleReconnect(card: BoardCard) {
    if (!card.reconnectId) return;
    setBusyId(card.id);
    void reconnectGoogleConnection(tenantId, card.reconnectId).then(
      (path) => window.location.assign(path),
      () => {
        setBusyId(null);
        onNavigate('/personal/accounts');
      }
    );
  }

  const ready = state.phase === 'ready' ? state : null;

  const cardsByColumn = useMemo(() => {
    if (!ready) return new Map<string, BoardCard[]>();
    const m = new Map<string, BoardCard[]>();
    for (const c of ready.columns) {
      const all = cardsForColumn(c, ready.briefing, { nextEvent, audienceUnanswered, systemsSummary: ready.systemsSummary });
      // A card whose done receipt expired is gone from its column — "it
      // expires after lever cockpit.board.done_ttl_minutes" (§H1 "Receipts
      // and Undo"), not reverted to pending.
      m.set(
        c.id,
        all.filter((card) => !hiddenCardIds[card.id])
      );
    }
    return m;
  }, [ready, nextEvent, audienceUnanswered, hiddenCardIds]);

  // Sort/reason read a DONE card's needsYou as resolved — a just-approved
  // proposal stops pinning its column to the front, even before the next
  // `/api/today` refetch catches up server-side.
  const cardsByColumnForSort = useMemo(() => {
    const m = new Map<string, BoardCard[]>();
    for (const [id, cards] of cardsByColumn) {
      m.set(
        id,
        cards.map((c) => (doneById[c.id] ? { ...c, needsYou: false } : c))
      );
    }
    return m;
  }, [cardsByColumn, doneById]);

  const sorted = useMemo(() => (ready ? sortColumns(ready.columns, cardsByColumnForSort) : []), [ready, cardsByColumnForSort]);

  // The one move both ⌘←/⌘→ AND the visible ‹/› row controls (below) make —
  // written once so the keyboard shortcut and the on-screen buttons can
  // never drift: "next"/"previous" always means exactly one column,
  // scrolled to its own snap point (CSS `scroll-snap-align: start` does
  // the actual alignment; this only picks WHICH column and asks the
  // browser to bring it into view).
  function moveFocusColumn(direction: -1 | 1) {
    if (sorted.length === 0) return;
    const ids = sorted.map((c) => c.id);
    const current = focusColumnId ? ids.indexOf(focusColumnId) : -1;
    // No focus column yet (nothing touched this session) reads as "the
    // first column is current" (base 0) — "next" from there moves to the
    // SECOND column, same as it would from any other explicit current.
    // ⚑ Found by the walk's own scrollLeft assertion (H1b review round 2,
    // 2026-10-16): the old `base + (current < 0 ? 0 : 1)` kept `next`
    // pinned at index 0 on the very first press/click, so the first ›/⌘→
    // of a session silently did nothing — a pre-existing H1a bug, caught
    // only once the ›/‹ buttons made the move visibly testable.
    const base = current < 0 ? 0 : current;
    const next = direction < 0 ? Math.max(0, base - 1) : Math.min(ids.length - 1, base + 1);
    const id = ids[next]!;
    setFocusColumnId(id);
    const el = rowRef.current?.querySelector<HTMLElement>(`[data-testid="column-${id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  }

  // ⌘← / ⌘→ — the laptop's own keyboard path, §H1 layout row. Separate
  // from a column's own focus CARD (ColumnView's "Next"): this one
  // scrolls/highlights which column is current.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.metaKey && !e.ctrlKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      moveFocusColumn(e.key === 'ArrowLeft' ? -1 : 1);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorted, focusColumnId]);

  // "Also in H1b" (atlas-review, 2026-10-16) — the row's own ‹/› edge
  // controls: real buttons, never a wheel hijack (no 'wheel' listener
  // exists anywhere in this file — vertical scroll is left alone). Each
  // reads the row's OWN scroll geometry so "hidden when there is nothing
  // further" is the truth of the DOM, not a guess from the column count
  // (narrower widths show fewer columns per the same scrollWidth).
  const [rowEdges, setRowEdges] = useState({ atStart: true, atEnd: true });
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    function update() {
      const row = rowRef.current;
      if (!row) return;
      setRowEdges({
        atStart: row.scrollLeft <= 1,
        atEnd: row.scrollLeft + row.clientWidth >= row.scrollWidth - 1,
      });
    }
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [sorted]);

  if (state.phase === 'loading') {
    return (
      <div class="chome">
        <div class="chome__skeleton" aria-hidden="true">
          <div class="chome__skeleton-gauges" />
          <div class="chome__skeleton-cols" />
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div class="chome">
        <div class="chome__say">
          The board could not be read.
          <br />
          <span class="chome__say-soft">{state.message}</span>
        </div>
      </div>
    );
  }

  const gauges = state.briefing.meters.facts.filter((f) => f.capNum !== null);

  const acts: ColumnActs = {
    doneById,
    errorById,
    busyId,
    onApproveProposal: (card) => void handleApproveProposal(card),
    onApproveProgramme: (card) => void handleApproveProgramme(card),
    onDoneForNow: handleDoneForNow,
    onUndo: handleUndo,
    onReconnect: handleReconnect,
  };

  return (
    <div class="chome">
      {gauges.length > 0 ? (
        <div class="chome__gauges" role="group" aria-label="Meters">
          {gauges.map((f) => (
            <Gauge
              key={`${f.section}:${f.key}`}
              fact={f}
              open={openGauge === `${f.section}:${f.key}`}
              onToggle={() => setOpenGauge((cur) => (cur === `${f.section}:${f.key}` ? null : `${f.section}:${f.key}`))}
            />
          ))}
        </div>
      ) : null}
      <div class="chome__row-wrap">
        <div
          class="chome__row"
          ref={rowRef}
          onScroll={() => {
            // H2 — a pure row drag/swipe with no focus-column change
            // still has to keep the place; the effect above only fires
            // when `focusColumnId` itself changes.
            if (!boardPlaceRestored.current) return;
            writeCockpitPlace('home', { focusColumnId, rowScrollLeft: rowRef.current?.scrollLeft ?? 0 });
          }}
        >
          {sorted.map((column) => {
            const cards = cardsByColumn.get(column.id) ?? [];
            const reason = sortReason(column, sorted, cardsByColumnForSort);
            return (
              <ColumnView
                key={column.id}
                column={column}
                cards={cards}
                reason={reason}
                reasonOpen={reasonOpenFor === column.id}
                focusIndex={focusByColumn[column.id] ?? 0}
                acts={acts}
                onOpenScreen={() => onNavigate(column.screen)}
                onOpenCard={(card) => onNavigate(card.link)}
                onNextCard={() =>
                  setFocusByColumn((prev) => ({ ...prev, [column.id]: (prev[column.id] ?? 0) + 1 }))
                }
                onShowReason={() => setReasonOpenFor(column.id)}
                onHideReason={() => setReasonOpenFor(null)}
              />
            );
          })}
        </div>
        {!rowEdges.atStart ? (
          <button
            type="button"
            class="chome__row-nav chome__row-nav--prev"
            aria-label="Previous column"
            onClick={() => moveFocusColumn(-1)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              moveFocusColumn(-1);
            }}
          >
            ‹
          </button>
        ) : null}
        {!rowEdges.atEnd ? (
          <button
            type="button"
            class="chome__row-nav chome__row-nav--next"
            aria-label="Next column"
            onClick={() => moveFocusColumn(1)}
            onKeyDown={(e: KeyboardEvent) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              moveFocusColumn(1);
            }}
          >
            ›
          </button>
        ) : null}
      </div>
    </div>
  );
}
